import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Container,
  Fab,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import useListTitle from "hooks/useListTitle";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  BaseFilter,
  ListRequestOfBaseFilter,
} from "services/KnowfullyAdminClient";

const RolesList = () => {
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const { formatMessage: t } = useIntl();
  const { roleDetailsUrl, roleCreateUrl, roleService } = useGlobalContext();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);

  useListTitle("rolesLabel");

  const { isError, data, error, isFetching } = useQuery(
    ["roles", order, orderBy, page, rowsPerPage, debouncedSearchTerm],
    () =>
      roleService.getRoles(
        ListRequestOfBaseFilter.fromJS({
          sortField: orderBy,
          sortDescending: order === "desc",
          pageSize: rowsPerPage,
          pageNumber: page,
          parameters: BaseFilter.fromJS({
            searchText: debouncedSearchTerm,
          }),
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "roleLabel" }),
    },
  ];

  const roles = data?.data?.map((r) => ({ ...r, id: r.id! })) ?? [];

  const onClick = (id: string) => {
    navigate(roleDetailsUrl(id));
  };

  return (
    <Container>
      <Box mb={3} textAlign="center">
        <Link to={roleCreateUrl()} style={{ textDecoration: "none" }}>
          <Fab aria-label="add" color="primary">
            <AddIcon />
          </Fab>
        </Link>
      </Box>

      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          value={searchTerm ?? ""}
          placeholder={t({ id: "searchPlaceholder" })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setSearchTerm("")}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: roles,
          loading: isFetching,
          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => roleDetailsUrl(id)),
          ],
        }}
      />
    </Container>
  );
};

export default RolesList;
